"use client";

import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";
import OurStoryMobileWrapper from "./OurStoryMobileWrapper";

interface TimelineItem {
  year: string;
  text: string;
}

interface OurStoryClientProps {
  data: {
    tagline: string;
    title: string;
    description: string;
    timeline: TimelineItem[];
  };
}

export default function OurStoryClient({ data }: OurStoryClientProps) {
  const { tagline, title, description, timeline } = data;
  
  const sectionRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const timelineCards = useRef<NodeListOf<HTMLElement> | null>(null);
  const storyTexts = useRef<NodeListOf<HTMLElement> | null>(null);

  useEffect(() => {
    const mm = gsap.matchMedia();

    
    mm.add("(min-width: 768px)", () => {
      if (!sectionRef.current || !progressBarRef.current) return;

      const section = sectionRef.current!;
      const progressBar = progressBarRef.current!;

      timelineCards.current = section.querySelectorAll(".story-timeline-card");
      storyTexts.current = section.querySelectorAll(".story-text-item");

      const ctx = gsap.context(() => {
        gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: "bottom top",
            scrub: 1,
            pin: true,
            anticipatePin: 1,
            onUpdate: (self) => {
              const progress = self.progress;
              const totalItems = timeline.length;

              const totalWidth = 100;
              const oneCardSpace = 100 / totalItems;
              const startOffset = oneCardSpace / 2;
              const endOffset = oneCardSpace / 2;
              const effectiveWidth = totalWidth - startOffset - endOffset;

              if (progressBar) {
                progressBar.style.width = `calc(${startOffset}% + ${
                  progress * effectiveWidth
                }%)`;
              }

              const activeIndex = Math.floor(progress * (totalItems - 0.001));

              timelineCards.current?.forEach((card, index) => {
                if (index <= activeIndex) {
                  gsap.to(card, {
                    color: "#f6842b",
                    duration: 0.3,
                    ease: "power2.out",
                  });
                } else {
                  gsap.to(card, {
                    color: "#60a5fa",
                    duration: 0.3,
                    ease: "power2.out",
                  });
                }
              });

              storyTexts.current?.forEach((text, index) => {
                if (index === activeIndex) {
                  gsap.to(text, {
                    opacity: 1,
                    y: 0,
                    duration: 0.4,
                    ease: "power2.out",
                  });
                } else {
                  gsap.to(text, {
                    opacity: 0,
                    y: 10,
                    duration: 0.4,
                    ease: "power2.out",
                  });
                }
              });
            },
          },
        });

        gsap.set(progressBar, { width: "0%" });
        gsap.set(timelineCards.current, { color: "#60a5fa" });
        gsap.set(storyTexts.current, { opacity: 0, y: 10 });
        if (storyTexts.current?.[0]) {
          gsap.set(storyTexts.current[0], { opacity: 1, y: 0 });
        }
      }, section);

      return () => ctx.revert();
    });

    return () => mm.revert();
  }, [timeline.length]);

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!headerRef.current) return;

    const ctx = gsap.context(() => {
      const elements = headerRef.current?.querySelectorAll("[data-animate]");

      if (elements) {
        gsap.fromTo(
          elements,
          {
            opacity: 0,
            y: 30,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: "power2.out",
            scrollTrigger: {
              trigger: headerRef.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }, headerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="relative bg-primary-dark ">
      {}
      <div
        ref={sectionRef}
        className="hidden md:flex sticky top-0 h-screen overflow-hidden items-center justify-center"
      >
        <div ref={headerRef} className="w-full max-w-6xl mx-auto px-4 md:px-8">
          {tagline && (
            <div data-animate className="max-w-xl mx-auto text-center ">
              <div className="inline-flex items-center gap-2 bg-navy-700/50 text-sm font-medium px-3 py-1 rounded-full mb-4">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="text-light">{tagline}</span>
              </div>
              {title && (
                <h2
                  data-animate
                  className="text-4xl md:text-5xl font-bold leading-[48px] text-[#F8FAFC]"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  data-animate
                  className="mt-4 text-lg text-[#BFDBFE] leading-[28px] max-w-lg mx-auto"
                >
                  {description}
                </p>
              )}
            </div>
          )}

          {timeline && timeline.length > 0 && (
            <div className="w-full relative">
              <div className="absolute top-1/2 left-0 w-full h-0.5 bg-navy-700/50">
                <div
                  ref={progressBarRef}
                  className="h-full bg-accent transition-[width] duration-100"
                  style={{ width: "0%" }}
                />
              </div>

              <div className="relative flex justify-between items-center w-full my-[64px]">
                {timeline.map((item, index) => (
                  <div
                    key={index}
                    className="story-timeline-card flex flex-col items-center text-navy-100 transition-colors duration-400"
                    data-year={item.year}
                  >
                    {item.year && (
                      <div className="year text-lg font-semibold mb-3">
                        {item.year}
                      </div>
                    )}
                    <div
                      className="w-4 h-4 rounded-full bg-current border-2 z-10"
                      style={{ borderColor: "var(--color-navy-900)" }}
                    ></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {timeline && timeline.length > 0 && (
            <div className="relative text-center">
              {timeline.map((item, index) => (
                <div
                  key={index}
                  className="my-[32px] story-text-item absolute inset-0 flex items-center justify-center px-4 transition-all duration-400"
                  style={{
                    opacity: index === 0 ? 1 : 0,
                    transform:
                      index === 0 ? "translateY(0)" : "translateY(10px)",
                  }}
                >
                  {item.text && (
                    <p className="text-[#DBEAFE] text-lg max-w-2xl mx-auto">
                      {item.text}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {}

      <OurStoryMobileWrapper data={data} />
    </section>
  );
}
